import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
    LayoutDashboard, Package, ShoppingCart, Users, CalendarDays, 
    Plus, Edit, Trash2, Loader2, Euro, TrendingUp, Clock,
    CheckCircle, XCircle, AlertCircle, Upload, Image, X, Briefcase, Percent,
    FlaskConical, Send, Thermometer, Gift, Coins, Mail, Truck, PartyPopper, Settings, BookOpen, Beaker, FileText, Download, ExternalLink, Minus
} from 'lucide-react';
import { apiRequest, formatPrice, formatDate, API_URL } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import SalesReportPage from './SalesReportPage';
import MarketingAdminPage from './MarketingAdminPage';
import SettingsSection from '../components/admin/SettingsSection';
import BulkImageUpload from '../components/admin/BulkImageUpload';
import ArticlesSection from '../components/admin/ArticlesSection';
import SpinTouchSection from '../components/admin/SpinTouchSection';
import AgendaSection from '../components/admin/AgendaSection';

// Wrapper component for sales report in admin
function SalesReportSection() {
    return <SalesReportPage />;
}

// Order status configuration
const ORDER_STATUSES = [
    { value: 'pending', label: 'En attente', color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { value: 'confirmed', label: 'Confirmée', color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { value: 'preparing', label: 'En préparation', color: 'text-purple-500', bg: 'bg-purple-500/10' },
    { value: 'ready', label: 'Prête', color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    { value: 'shipped', label: 'Expédiée', color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
    { value: 'delivered', label: 'Livrée', color: 'text-green-500', bg: 'bg-green-500/10' },
    { value: 'cancelled', label: 'Annulée', color: 'text-red-500', bg: 'bg-red-500/10' }
];

// Admin Orders Section Component
function AdminOrdersSection() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const [sendEmail, setSendEmail] = useState(true);
    const [updating, setUpdating] = useState(false);
    
    // Tracking state
    const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
    const [trackingNumber, setTrackingNumber] = useState('');
    const [trackingSendEmail, setTrackingSendEmail] = useState(true);
    const [trackingUpdating, setTrackingUpdating] = useState(false);

    useEffect(() => {
        loadOrders();
    }, [statusFilter]);

    const loadOrders = async () => {
        setLoading(true);
        try {
            const url = statusFilter === 'all' ? '/admin/orders' : `/admin/orders?status=${statusFilter}`;
            const data = await apiRequest(url);
            setOrders(data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async () => {
        if (!selectedOrder || !newStatus) return;
        
        setUpdating(true);
        try {
            await apiRequest(`/admin/orders/${selectedOrder.order_id}/status`, {
                method: 'PUT',
                body: JSON.stringify({
                    status: newStatus,
                    message: statusMessage,
                    send_email: sendEmail
                })
            });
            toast.success('Statut mis à jour avec succès');
            setUpdateDialogOpen(false);
            setSelectedOrder(null);
            setNewStatus('');
            setStatusMessage('');
            loadOrders();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setUpdating(false);
        }
    };

    const getStatusConfig = (status) => {
        return ORDER_STATUSES.find(s => s.value === status) || ORDER_STATUSES[0];
    };

    const handleExportCSV = async () => {
        try {
            const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/orders${params}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
            });
            if (!response.ok) throw new Error('Erreur lors de l\'export');
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `commandes_oca_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
            toast.success('Export CSV téléchargé');
        } catch (error) {
            toast.error('Erreur lors de l\'export CSV');
        }
    };

    const handleAddTracking = async () => {
        if (!selectedOrder || !trackingNumber.trim()) return;
        
        setTrackingUpdating(true);
        try {
            await apiRequest(`/admin/orders/${selectedOrder.order_id}/tracking?tracking_number=${encodeURIComponent(trackingNumber.trim())}&send_email=${trackingSendEmail}`, {
                method: 'PUT'
            });
            toast.success('Numéro de suivi ajouté ! Email envoyé au client.');
            setTrackingDialogOpen(false);
            setSelectedOrder(null);
            setTrackingNumber('');
            loadOrders();
        } catch (error) {
            toast.error(error.message || 'Erreur lors de l\'ajout du suivi');
        } finally {
            setTrackingUpdating(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Filters */}
            <div className="flex items-center gap-4 flex-wrap">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-48">
                        <SelectValue placeholder="Filtrer par statut" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tous les statuts</SelectItem>
                        {ORDER_STATUSES.map(status => (
                            <SelectItem key={status.value} value={status.value}>
                                {status.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button variant="outline" onClick={loadOrders} disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Actualiser'}
                </Button>
                <Button variant="outline" onClick={handleExportCSV}>
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                </Button>
            </div>

            {/* Orders List */}
            <Card className="glass rounded-2xl">
                <CardHeader>
                    <CardTitle>Commandes ({orders.length})</CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin" />
                        </div>
                    ) : orders.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">Aucune commande</p>
                    ) : (
                        <div className="space-y-4">
                            {orders.map(order => {
                                const statusConfig = getStatusConfig(order.status);
                                return (
                                    <div key={order.order_id} className="p-4 bg-muted/50 rounded-xl">
                                        <div className="flex items-start justify-between gap-4 flex-wrap">
                                            <div className="flex-1 min-w-[200px]">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-mono font-semibold">#{order.order_id}</span>
                                                    <Badge className={`${statusConfig.bg} ${statusConfig.color}`}>
                                                        {statusConfig.label}
                                                    </Badge>
                                                </div>
                                                <p className="text-sm text-muted-foreground">{order.user_name} • {order.user_email}</p>
                                                <p className="text-sm text-muted-foreground">{formatDate(order.created_at)}</p>
                                                {order.tracking_number && (
                                                    <a 
                                                        href={order.tracking_url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mt-1"
                                                    >
                                                        <Truck className="h-3 w-3" />
                                                        Suivi: {order.tracking_number}
                                                        <ExternalLink className="h-3 w-3" />
                                                    </a>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <p className="font-semibold text-lg">{formatPrice(order.total)}</p>
                                                <p className="text-sm text-muted-foreground">{order.items?.length || 0} article(s)</p>
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedOrder(order);
                                                        setNewStatus(order.status);
                                                        setUpdateDialogOpen(true);
                                                    }}
                                                >
                                                    <Edit className="h-4 w-4 mr-1" />
                                                    Statut
                                                </Button>
                                                {!order.tracking_number && order.delivery_method === 'delivery' && (
                                                    <Button
                                                        variant="default"
                                                        size="sm"
                                                        className="bg-blue-600 hover:bg-blue-700"
                                                        onClick={() => {
                                                            setSelectedOrder(order);
                                                            setTrackingNumber('');
                                                            setTrackingDialogOpen(true);
                                                        }}
                                                    >
                                                        <Truck className="h-4 w-4 mr-1" />
                                                        Suivi
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Update Status Dialog */}
            <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Mettre à jour le statut</DialogTitle>
                    </DialogHeader>
                    {selectedOrder && (
                        <div className="space-y-4 py-4">
                            <div className="p-3 bg-muted/50 rounded-lg">
                                <p className="font-mono font-semibold">#{selectedOrder.order_id}</p>
                                <p className="text-sm text-muted-foreground">{selectedOrder.user_name}</p>
                            </div>
                            
                            <div>
                                <Label>Nouveau statut</Label>
                                <Select value={newStatus} onValueChange={setNewStatus}>
                                    <SelectTrigger className="mt-1">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ORDER_STATUSES.map(status => (
                                            <SelectItem key={status.value} value={status.value}>
                                                {status.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div>
                                <Label>Message (optionnel)</Label>
                                <Textarea
                                    value={statusMessage}
                                    onChange={(e) => setStatusMessage(e.target.value)}
                                    placeholder="Message pour le client..."
                                    className="mt-1"
                                />
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="sendEmail"
                                    checked={sendEmail}
                                    onChange={(e) => setSendEmail(e.target.checked)}
                                    className="rounded"
                                />
                                <Label htmlFor="sendEmail" className="flex items-center gap-2 cursor-pointer">
                                    <Mail className="h-4 w-4" />
                                    Envoyer un email au client
                                </Label>
                            </div>
                            
                            <Button
                                onClick={handleUpdateStatus}
                                disabled={updating || !newStatus}
                                className="w-full"
                            >
                                {updating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                Mettre à jour
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Tracking Dialog */}
            <Dialog open={trackingDialogOpen} onOpenChange={setTrackingDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Truck className="h-5 w-5 text-blue-600" />
                            Ajouter le suivi Chronopost
                        </DialogTitle>
                    </DialogHeader>
                    {selectedOrder && (
                        <div className="space-y-4 py-4">
                            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                <p className="font-mono font-semibold text-blue-700 dark:text-blue-300">#{selectedOrder.order_id}</p>
                                <p className="text-sm text-blue-600 dark:text-blue-400">{selectedOrder.user_name} • {selectedOrder.user_email}</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {selectedOrder.shipping_address?.address}, {selectedOrder.shipping_address?.city} {selectedOrder.shipping_address?.postal_code}
                                </p>
                            </div>
                            
                            <div>
                                <Label className="text-base font-semibold">Numéro de suivi Chronopost</Label>
                                <Input
                                    value={trackingNumber}
                                    onChange={(e) => setTrackingNumber(e.target.value.toUpperCase())}
                                    placeholder="Ex: 1234567890ABCD"
                                    className="mt-2 text-lg font-mono tracking-wider"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    Copiez le numéro depuis votre bordereau Chronopost
                                </p>
                            </div>
                            
                            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                                <input
                                    type="checkbox"
                                    id="trackingSendEmail"
                                    checked={trackingSendEmail}
                                    onChange={(e) => setTrackingSendEmail(e.target.checked)}
                                    className="rounded"
                                />
                                <Label htmlFor="trackingSendEmail" className="flex items-center gap-2 cursor-pointer">
                                    <Mail className="h-4 w-4" />
                                    Envoyer l'email de suivi au client
                                </Label>
                            </div>

                            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                                <p className="text-sm text-green-700 dark:text-green-300">
                                    <strong>📧 L'email contiendra :</strong>
                                </p>
                                <ul className="text-xs text-green-600 dark:text-green-400 mt-1 space-y-0.5">
                                    <li>• Le numéro de suivi</li>
                                    <li>• Un bouton pour suivre le colis sur Chronopost</li>
                                    <li>• Le détail de la commande</li>
                                </ul>
                            </div>
                            
                            <Button
                                onClick={handleAddTracking}
                                disabled={trackingUpdating || !trackingNumber.trim()}
                                className="w-full bg-blue-600 hover:bg-blue-700"
                            >
                                {trackingUpdating ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : (
                                    <Truck className="h-4 w-4 mr-2" />
                                )}
                                Enregistrer et notifier le client
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default function AdminPage() {
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    
    const [stats, setStats] = useState(null);
    const [orders, setOrders] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [products, setProducts] = useState([]);
    const [users, setUsers] = useState([]);
    const [proRequests, setProRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('dashboard');

    // SpinTouch state
    const [spinTouchDialogOpen, setSpinTouchDialogOpen] = useState(false);
    const [spinTouchAnalyses, setSpinTouchAnalyses] = useState([]);
    const [spinTouchUsers, setSpinTouchUsers] = useState([]);
    const [spinTouchLoading, setSpinTouchLoading] = useState(false);
    
    // Loyalty points state
    const [loyaltyDialogOpen, setLoyaltyDialogOpen] = useState(false);
    const [loyaltySelectedUser, setLoyaltySelectedUser] = useState('');
    const [loyaltyPointsToAdd, setLoyaltyPointsToAdd] = useState('');
    const [loyaltyReason, setLoyaltyReason] = useState('');
    const [loyaltyLoading, setLoyaltyLoading] = useState(false);
    const [loyaltyAction, setLoyaltyAction] = useState('add'); // 'add' | 'remove'
    
    const [spinTouchForm, setSpinTouchForm] = useState({
        target_user_id: '',
        ph: '7.2',
        chlorine: '1.5',
        alkalinity: '100',
        stabilizer: '40',
        calcium: '250',
        salt: '0',
        iron: '0',
        phosphate: '0',
        copper: '0',
        temperature: '25',
        notes: '',
        send_email: true
    });

    // Product form state
    const [productDialogOpen, setProductDialogOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [productForm, setProductForm] = useState({
        name: '', description: '', category: 'chimie', price: '', 
        image_url: '', dosage_info: '', stock: '100', brand: 'CTX Pro'
    });
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);

    useEffect(() => {
        if (authLoading) return; // Wait for auth to be checked
        if (!user) {
            navigate('/auth');
            return;
        }
        loadAdminData();
    }, [user, authLoading]);

    const loadAdminData = async () => {
        setLoading(true);
        try {
            const [statsData, ordersData, appointmentsData, productsData, usersData, proRequestsData] = await Promise.all([
                apiRequest('/admin/stats'),
                apiRequest('/admin/orders'),
                apiRequest('/admin/appointments'),
                apiRequest('/admin/products'),
                apiRequest('/admin/users'),
                apiRequest('/admin/pro-requests')
            ]);
            setStats(statsData);
            setOrders(ordersData);
            setAppointments(appointmentsData);
            setProducts(productsData);
            setUsers(usersData);
            setProRequests(proRequestsData);
            
            // Load SpinTouch data
            loadSpinTouchData();
        } catch (error) {
            if (error.message.includes('403') || error.message.includes('Admin')) {
                toast.error('Accès refusé - Vous devez être administrateur');
                navigate('/');
            } else {
                toast.error(error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const loadSpinTouchData = async () => {
        try {
            const [analyses, usersForSpinTouch] = await Promise.all([
                apiRequest('/admin/spintouch/analyses'),
                apiRequest('/admin/spintouch/users')
            ]);
            setSpinTouchAnalyses(analyses);
            setSpinTouchUsers(usersForSpinTouch);
        } catch (error) {
            console.error('SpinTouch data load error:', error);
        }
    };

    const handleSpinTouchSubmit = async () => {
        if (!spinTouchForm.target_user_id) {
            toast.error('Veuillez sélectionner un client');
            return;
        }
        
        setSpinTouchLoading(true);
        try {
            const data = {
                target_user_id: spinTouchForm.target_user_id,
                measurements: {
                    ph: parseFloat(spinTouchForm.ph),
                    chlorine: parseFloat(spinTouchForm.chlorine),
                    alkalinity: parseFloat(spinTouchForm.alkalinity),
                    stabilizer: parseFloat(spinTouchForm.stabilizer),
                    calcium: parseFloat(spinTouchForm.calcium),
                    salt: parseFloat(spinTouchForm.salt),
                    iron: parseFloat(spinTouchForm.iron),
                    phosphate: parseFloat(spinTouchForm.phosphate),
                    copper: parseFloat(spinTouchForm.copper)
                },
                temperature: parseFloat(spinTouchForm.temperature),
                notes: spinTouchForm.notes || null,
                send_email: spinTouchForm.send_email
            };
            
            const result = await apiRequest('/admin/spintouch/create', {
                method: 'POST',
                body: JSON.stringify(data)
            });
            
            toast.success('Analyse SpinTouch envoyée au client !');
            setSpinTouchDialogOpen(false);
            resetSpinTouchForm();
            loadSpinTouchData();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSpinTouchLoading(false);
        }
    };

    const resetSpinTouchForm = () => {
        setSpinTouchForm({
            target_user_id: '',
            ph: '7.2',
            chlorine: '1.5',
            alkalinity: '100',
            stabilizer: '40',
            calcium: '250',
            salt: '0',
            iron: '0',
            phosphate: '0',
            copper: '0',
            temperature: '25',
            notes: '',
            send_email: true
        });
    };

    const handleOrderStatusChange = async (orderId, newStatus) => {
        try {
            await apiRequest(`/admin/orders/${orderId}/status?status=${newStatus}`, { method: 'PUT' });
            toast.success('Statut mis à jour');
            loadAdminData();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleAppointmentStatusChange = async (appointmentId, newStatus) => {
        try {
            await apiRequest(`/admin/appointments/${appointmentId}/status?status=${newStatus}`, { method: 'PUT' });
            toast.success('Statut mis à jour');
            loadAdminData();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleProductSubmit = async () => {
        try {
            const productData = {
                ...productForm,
                price: parseFloat(productForm.price),
                stock: parseInt(productForm.stock)
            };

            if (editingProduct) {
                await apiRequest(`/admin/products/${editingProduct.product_id}`, {
                    method: 'PUT',
                    body: JSON.stringify(productData)
                });
                toast.success('Produit mis à jour');
            } else {
                await apiRequest('/admin/products', {
                    method: 'POST',
                    body: JSON.stringify(productData)
                });
                toast.success('Produit créé');
            }
            setProductDialogOpen(false);
            setEditingProduct(null);
            resetProductForm();
            loadAdminData();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Format non supporté. Utilisez JPG, PNG, GIF ou WebP');
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Fichier trop volumineux (max 5MB)');
            return;
        }

        // Show preview
        const reader = new FileReader();
        reader.onload = (e) => setImagePreview(e.target.result);
        reader.readAsDataURL(file);

        // Upload file
        setUploadingImage(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const token = localStorage.getItem('auth_token');
            const response = await fetch(`${API_URL}/upload/image`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Erreur upload');
            }

            const data = await response.json();
            // Use full URL for the uploaded image (permanent storage)
            // Remove /api prefix since API_URL already includes it
            const fullUrl = `${API_URL}${data.url.replace('/api/', '/')}`;
            setProductForm(p => ({ ...p, image_url: fullUrl }));
            setImagePreview(fullUrl);
            toast.success('Image uploadée !');
        } catch (error) {
            toast.error(error.message);
            setImagePreview(null);
        } finally {
            setUploadingImage(false);
        }
    };

    const clearImagePreview = () => {
        setImagePreview(null);
        setProductForm(p => ({ ...p, image_url: '' }));
    };

    const handleDeleteProduct = async (productId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;
        try {
            await apiRequest(`/admin/products/${productId}`, { method: 'DELETE' });
            toast.success('Produit supprimé');
            loadAdminData();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleProStatusChange = async (userId, action, discount = 0) => {
        try {
            let updateData = {};
            if (action === 'approve') {
                updateData = { is_pro: true, pro_status: 'approved', discount_percentage: discount };
            } else if (action === 'reject') {
                updateData = { is_pro: false, pro_status: 'rejected' };
            } else if (action === 'update_discount') {
                updateData = { discount_percentage: discount };
            }
            
            await apiRequest(`/admin/users/${userId}/pro`, {
                method: 'PUT',
                body: JSON.stringify(updateData)
            });
            toast.success('Statut professionnel mis à jour');
            loadAdminData();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const openEditProduct = (product) => {
        setEditingProduct(product);
        setProductForm({
            name: product.name,
            description: product.description,
            category: product.category,
            price: product.price.toString(),
            image_url: product.image_url,
            dosage_info: product.dosage_info || '',
            stock: product.stock.toString(),
            brand: product.brand
        });
        setImagePreview(product.image_url);
        setProductDialogOpen(true);
    };

    const resetProductForm = () => {
        setProductForm({
            name: '', description: '', category: 'chimie', price: '',
            image_url: '', dosage_info: '', stock: '100', brand: 'CTX Pro'
        });
        setImagePreview(null);
    };

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'bg-amber-500/10 text-amber-500',
            confirmed: 'bg-blue-500/10 text-blue-500',
            completed: 'bg-emerald-500/10 text-emerald-500',
            cancelled: 'bg-red-500/10 text-red-500',
            paid: 'bg-emerald-500/10 text-emerald-500',
            shipped: 'bg-blue-500/10 text-blue-500',
            delivered: 'bg-emerald-500/10 text-emerald-500'
        };
        const labels = {
            pending: 'En attente',
            confirmed: 'Confirmé',
            completed: 'Terminé',
            cancelled: 'Annulé',
            paid: 'Payé',
            shipped: 'Expédié',
            delivered: 'Livré'
        };
        return <Badge className={`${styles[status] || 'bg-muted'} border-0`}>{labels[status] || status}</Badge>;
    };

    if (loading || authLoading) {
        return (
            <div className="min-h-screen pt-20 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="admin-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <LayoutDashboard className="h-8 w-8 text-primary" />
                        <h1 className="font-heading text-3xl font-bold">Administration</h1>
                    </div>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <div className="overflow-x-auto pb-2 mb-6 -mx-4 px-4 sm:mx-0 sm:px-0">
                        <TabsList className="inline-flex min-w-max gap-1">
                            <TabsTrigger value="dashboard" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                                <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                <span className="hidden sm:inline">Tableau de bord</span>
                                <span className="sm:hidden">Accueil</span>
                            </TabsTrigger>
                            <TabsTrigger value="orders" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                                <ShoppingCart className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                Commandes
                            </TabsTrigger>
                            <TabsTrigger value="appointments" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                                <CalendarDays className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                <span className="hidden sm:inline">Rendez-vous</span>
                                <span className="sm:hidden">RDV</span>
                            </TabsTrigger>
                            <TabsTrigger value="spintouch" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                                <FlaskConical className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                SpinTouch
                            </TabsTrigger>
                            <TabsTrigger value="pro" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                                <Briefcase className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                Pros ({proRequests.length})
                            </TabsTrigger>
                            <TabsTrigger value="products" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                                <Package className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                Produits
                            </TabsTrigger>
                            <TabsTrigger value="sales" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                                <Euro className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                Ventes
                            </TabsTrigger>
                            <TabsTrigger value="loyalty" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                                <Gift className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                Fidélité
                            </TabsTrigger>
                            <TabsTrigger value="users" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                                <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                <span className="hidden sm:inline">Utilisateurs</span>
                                <span className="sm:hidden">Users</span>
                            </TabsTrigger>
                            <TabsTrigger value="marketing" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                                <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                Marketing
                            </TabsTrigger>
                            <TabsTrigger value="settings" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                                <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                <span className="hidden sm:inline">Paramètres</span>
                                <span className="sm:hidden">Config</span>
                            </TabsTrigger>
                            <TabsTrigger value="images" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                                <Image className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                <span className="hidden sm:inline">Images</span>
                                <span className="sm:hidden">Img</span>
                            </TabsTrigger>
                            <TabsTrigger value="articles" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                                <BookOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                <span className="hidden sm:inline">Articles</span>
                                <span className="sm:hidden">Blog</span>
                            </TabsTrigger>
                            <TabsTrigger value="spintouch" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                                <Beaker className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                <span className="hidden sm:inline">SpinTouch</span>
                                <span className="sm:hidden">Spin</span>
                            </TabsTrigger>
                        </TabsList>
                    </div>

                    {/* Dashboard */}
                    <TabsContent value="dashboard">
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                            <Card className="glass rounded-2xl">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Chiffre d&apos;affaires</p>
                                            <p className="font-heading text-2xl font-bold text-primary">
                                                {formatPrice(stats?.total_revenue || 0)}
                                            </p>
                                        </div>
                                        <Euro className="h-10 w-10 text-primary/20" />
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="glass rounded-2xl">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Commandes</p>
                                            <p className="font-heading text-2xl font-bold">{stats?.total_orders || 0}</p>
                                        </div>
                                        <ShoppingCart className="h-10 w-10 text-primary/20" />
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="glass rounded-2xl">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">RDV en attente</p>
                                            <p className="font-heading text-2xl font-bold text-amber-500">
                                                {stats?.pending_appointments || 0}
                                            </p>
                                        </div>
                                        <Clock className="h-10 w-10 text-amber-500/20" />
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="glass rounded-2xl">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Utilisateurs</p>
                                            <p className="font-heading text-2xl font-bold">{stats?.total_users || 0}</p>
                                        </div>
                                        <Users className="h-10 w-10 text-primary/20" />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Recent activity */}
                        <div className="grid lg:grid-cols-2 gap-6">
                            <Card className="glass rounded-2xl">
                                <CardHeader>
                                    <CardTitle className="text-lg">Dernières commandes</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {orders.slice(0, 5).map(order => (
                                            <div key={order.order_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                                                <div>
                                                    <p className="font-mono text-sm">#{order.order_id.slice(-8)}</p>
                                                    <p className="text-xs text-muted-foreground">{formatDate(order.created_at)}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-semibold">{formatPrice(order.total)}</p>
                                                    {getStatusBadge(order.status)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="glass rounded-2xl">
                                <CardHeader>
                                    <CardTitle className="text-lg">Rendez-vous en attente</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {appointments.filter(a => a.status === 'pending').slice(0, 5).map(apt => (
                                            <div key={apt.appointment_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                                                <div>
                                                    <p className="font-semibold">{apt.name}</p>
                                                    <p className="text-xs text-muted-foreground">{apt.service_type} - {apt.preferred_date}</p>
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button size="sm" className="rounded-full h-8" onClick={() => handleAppointmentStatusChange(apt.appointment_id, 'confirmed')}>
                                                        <CheckCircle className="h-4 w-4" />
                                                    </Button>
                                                    <Button size="sm" variant="destructive" className="rounded-full h-8" onClick={() => handleAppointmentStatusChange(apt.appointment_id, 'cancelled')}>
                                                        <XCircle className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                        {appointments.filter(a => a.status === 'pending').length === 0 && (
                                            <p className="text-center text-muted-foreground py-4">Aucun rendez-vous en attente</p>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* Orders */}
                    <TabsContent value="orders">
                        <Card className="glass rounded-2xl">
                            <CardHeader>
                                <CardTitle>Toutes les commandes</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {orders.map(order => (
                                        <div key={order.order_id} className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3">
                                                    <p className="font-mono font-semibold">#{order.order_id.slice(-8)}</p>
                                                    {getStatusBadge(order.status)}
                                                    {getStatusBadge(order.payment_status)}
                                                </div>
                                                <p className="text-sm text-muted-foreground mt-1">
                                                    {order.items.length} article(s) • {order.delivery_type === 'delivery' ? 'Livraison' : 'Retrait'} • {formatDate(order.created_at)}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <p className="font-heading font-bold text-lg">{formatPrice(order.total)}</p>
                                                {order.payment_status === 'paid' && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="rounded-full gap-1"
                                                        onClick={() => {
                                                            const token = localStorage.getItem('auth_token');
                                                            fetch(`${API_URL}/api/invoices/admin/${order.order_id}`, {
                                                                headers: { 'Authorization': `Bearer ${token}` }
                                                            })
                                                            .then(res => res.blob())
                                                            .then(blob => {
                                                                const url = window.URL.createObjectURL(blob);
                                                                const a = document.createElement('a');
                                                                a.href = url;
                                                                a.download = `Facture_${order.order_id}.pdf`;
                                                                a.click();
                                                                window.URL.revokeObjectURL(url);
                                                                toast.success('Facture téléchargée');
                                                            })
                                                            .catch(() => toast.error('Erreur téléchargement facture'));
                                                        }}
                                                    >
                                                        <FileText className="h-4 w-4" />
                                                        <span className="hidden md:inline">Facture</span>
                                                    </Button>
                                                )}
                                                <Select value={order.status} onValueChange={(value) => handleOrderStatusChange(order.order_id, value)}>
                                                    <SelectTrigger className="w-32 rounded-full">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="pending">En attente</SelectItem>
                                                        <SelectItem value="confirmed">Confirmée</SelectItem>
                                                        <SelectItem value="shipped">Expédiée</SelectItem>
                                                        <SelectItem value="delivered">Livrée</SelectItem>
                                                        <SelectItem value="cancelled">Annulée</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Appointments */}
                    <TabsContent value="appointments">
                        <AgendaSection />
                    </TabsContent>

                    {/* SpinTouch */}
                    <TabsContent value="spintouch">
                        <div className="space-y-6">
                            <Card className="glass rounded-2xl">
                                <CardHeader className="flex flex-row items-center justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <FlaskConical className="h-5 w-5 text-primary" />
                                            Analyses SpinTouch LaMotte
                                        </CardTitle>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Créez des analyses d'eau pour vos clients à partir des données SpinTouch
                                        </p>
                                    </div>
                                    <Dialog open={spinTouchDialogOpen} onOpenChange={setSpinTouchDialogOpen}>
                                        <DialogTrigger asChild>
                                            <Button className="rounded-full" onClick={resetSpinTouchForm}>
                                                <Plus className="h-4 w-4 mr-2" />
                                                Nouvelle analyse
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="max-w-lg">
                                            <DialogHeader>
                                                <DialogTitle className="flex items-center gap-2">
                                                    <FlaskConical className="h-5 w-5 text-primary" />
                                                    Nouvelle analyse SpinTouch
                                                </DialogTitle>
                                            </DialogHeader>
                                            <div className="grid gap-4 py-4">
                                                <div className="space-y-2">
                                                    <Label>Client destinataire</Label>
                                                    <Select 
                                                        value={spinTouchForm.target_user_id} 
                                                        onValueChange={(v) => setSpinTouchForm(p => ({ ...p, target_user_id: v }))}
                                                    >
                                                        <SelectTrigger><SelectValue placeholder="Sélectionner un client..." /></SelectTrigger>
                                                        <SelectContent>
                                                            {spinTouchUsers.map(u => (
                                                                <SelectItem key={u.user_id} value={u.user_id}>
                                                                    {u.name} ({u.email})
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                
                                                {/* Paramètres principaux */}
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>pH</Label>
                                                        <Input 
                                                            type="number" 
                                                            step="0.1"
                                                            value={spinTouchForm.ph} 
                                                            onChange={(e) => setSpinTouchForm(p => ({ ...p, ph: e.target.value }))}
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Chlore (ppm)</Label>
                                                        <Input 
                                                            type="number" 
                                                            step="0.1"
                                                            value={spinTouchForm.chlorine} 
                                                            onChange={(e) => setSpinTouchForm(p => ({ ...p, chlorine: e.target.value }))}
                                                        />
                                                    </div>
                                                </div>
                                                
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>Alcalinité TAC (ppm)</Label>
                                                        <Input 
                                                            type="number" 
                                                            value={spinTouchForm.alkalinity} 
                                                            onChange={(e) => setSpinTouchForm(p => ({ ...p, alkalinity: e.target.value }))}
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Stabilisant (ppm)</Label>
                                                        <Input 
                                                            type="number" 
                                                            value={spinTouchForm.stabilizer} 
                                                            onChange={(e) => setSpinTouchForm(p => ({ ...p, stabilizer: e.target.value }))}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>Calcaire TH (ppm)</Label>
                                                        <Input 
                                                            type="number" 
                                                            value={spinTouchForm.calcium} 
                                                            onChange={(e) => setSpinTouchForm(p => ({ ...p, calcium: e.target.value }))}
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Sel (g/L)</Label>
                                                        <Input 
                                                            type="number" 
                                                            step="0.1"
                                                            value={spinTouchForm.salt} 
                                                            onChange={(e) => setSpinTouchForm(p => ({ ...p, salt: e.target.value }))}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Métaux et minéraux */}
                                                <div className="grid grid-cols-3 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>Fer (ppm)</Label>
                                                        <Input 
                                                            type="number" 
                                                            step="0.01"
                                                            value={spinTouchForm.iron} 
                                                            onChange={(e) => setSpinTouchForm(p => ({ ...p, iron: e.target.value }))}
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Phosphate (ppm)</Label>
                                                        <Input 
                                                            type="number" 
                                                            step="0.01"
                                                            value={spinTouchForm.phosphate} 
                                                            onChange={(e) => setSpinTouchForm(p => ({ ...p, phosphate: e.target.value }))}
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Cuivre (ppm)</Label>
                                                        <Input 
                                                            type="number" 
                                                            step="0.01"
                                                            value={spinTouchForm.copper} 
                                                            onChange={(e) => setSpinTouchForm(p => ({ ...p, copper: e.target.value }))}
                                                        />
                                                    </div>
                                                </div>
                                                
                                                <div className="space-y-2">
                                                    <Label className="flex items-center gap-2">
                                                        <Thermometer className="h-4 w-4" />
                                                        Température (°C)
                                                    </Label>
                                                    <Input 
                                                        type="number" 
                                                        value={spinTouchForm.temperature} 
                                                        onChange={(e) => setSpinTouchForm(p => ({ ...p, temperature: e.target.value }))}
                                                    />
                                                </div>
                                                
                                                <div className="space-y-2">
                                                    <Label>Notes (optionnel)</Label>
                                                    <Textarea 
                                                        value={spinTouchForm.notes} 
                                                        onChange={(e) => setSpinTouchForm(p => ({ ...p, notes: e.target.value }))}
                                                        placeholder="Observations, contexte..."
                                                    />
                                                </div>

                                                {/* Option envoi email */}
                                                <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                                                    <input
                                                        type="checkbox"
                                                        id="send_email"
                                                        checked={spinTouchForm.send_email}
                                                        onChange={(e) => setSpinTouchForm(p => ({ ...p, send_email: e.target.checked }))}
                                                        className="h-4 w-4 rounded"
                                                    />
                                                    <Label htmlFor="send_email" className="cursor-pointer flex-1">
                                                        Envoyer les résultats par email au client
                                                    </Label>
                                                </div>
                                                
                                                <Button 
                                                    onClick={handleSpinTouchSubmit} 
                                                    className="rounded-full"
                                                    disabled={spinTouchLoading}
                                                >
                                                    {spinTouchLoading ? (
                                                        <>
                                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                            Analyse en cours...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Send className="h-4 w-4 mr-2" />
                                                            Créer et envoyer l'analyse
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                </CardHeader>
                                <CardContent>
                                    {spinTouchAnalyses.length === 0 ? (
                                        <div className="text-center py-12">
                                            <FlaskConical className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                                            <p className="text-muted-foreground">Aucune analyse SpinTouch créée</p>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                Créez une nouvelle analyse pour un client
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {spinTouchAnalyses.map(analysis => (
                                                <div key={analysis.analysis_id} className="p-4 bg-muted/50 rounded-xl">
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-3 mb-2">
                                                                <h4 className="font-semibold">
                                                                    {analysis.user_info?.name || 'Client'}
                                                                </h4>
                                                                <Badge className={`border-0 ${
                                                                    analysis.expert_analysis?.status === 'good' ? 'bg-emerald-500/10 text-emerald-500' :
                                                                    analysis.expert_analysis?.status === 'warning' ? 'bg-amber-500/10 text-amber-500' :
                                                                    analysis.expert_analysis?.status === 'critical' ? 'bg-red-500/10 text-red-500' :
                                                                    'bg-primary/10 text-primary'
                                                                }`}>
                                                                    {analysis.expert_analysis?.status === 'good' ? 'OK' :
                                                                     analysis.expert_analysis?.status === 'warning' ? 'Attention' :
                                                                     analysis.expert_analysis?.status === 'critical' ? 'Critique' : 'Analysé'}
                                                                </Badge>
                                                            </div>
                                                            <p className="text-sm text-muted-foreground">
                                                                {analysis.user_info?.email}
                                                            </p>
                                                            <div className="flex gap-4 mt-2 text-sm flex-wrap">
                                                                <span><strong>pH:</strong> {analysis.measurements?.ph}</span>
                                                                <span><strong>Cl:</strong> {analysis.measurements?.chlorine} ppm</span>
                                                                <span><strong>TAC:</strong> {analysis.measurements?.alkalinity} ppm</span>
                                                                <span><strong>Stab:</strong> {analysis.measurements?.stabilizer} ppm</span>
                                                                {analysis.measurements?.calcium > 0 && <span><strong>TH:</strong> {analysis.measurements?.calcium} ppm</span>}
                                                                {analysis.measurements?.salt > 0 && <span><strong>Sel:</strong> {analysis.measurements?.salt} g/L</span>}
                                                                {analysis.measurements?.iron > 0 && <span><strong>Fer:</strong> {analysis.measurements?.iron} ppm</span>}
                                                                {analysis.measurements?.phosphate > 0 && <span><strong>Phosphate:</strong> {analysis.measurements?.phosphate} ppm</span>}
                                                                {analysis.measurements?.copper > 0 && <span><strong>Cu:</strong> {analysis.measurements?.copper} ppm</span>}
                                                            </div>
                                                            {analysis.expert_analysis?.summary && (
                                                                <p className="text-sm mt-2 text-muted-foreground italic">
                                                                    "{analysis.expert_analysis.summary}"
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="text-right text-xs text-muted-foreground">
                                                            {formatDate(analysis.created_at)}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* Pro Accounts */}
                    <TabsContent value="pro">
                        <div className="space-y-6">
                            {/* Pending Requests */}
                            <Card className="glass rounded-2xl">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Clock className="h-5 w-5 text-amber-500" />
                                        Demandes en attente ({proRequests.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {proRequests.length === 0 ? (
                                        <p className="text-center text-muted-foreground py-8">Aucune demande en attente</p>
                                    ) : (
                                        <div className="space-y-4">
                                            {proRequests.map(req => (
                                                <div key={req.user_id} className="p-4 bg-muted/50 rounded-xl">
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-3 mb-2">
                                                                <h4 className="font-semibold">{req.company_name}</h4>
                                                                <Badge className="bg-amber-500/10 text-amber-500 border-0">En attente</Badge>
                                                            </div>
                                                            <p className="text-sm"><span className="text-muted-foreground">Contact:</span> {req.name} ({req.email})</p>
                                                            <p className="text-sm"><span className="text-muted-foreground">SIRET:</span> {req.siret}</p>
                                                            <p className="text-sm"><span className="text-muted-foreground">Tél:</span> {req.pro_phone}</p>
                                                            <p className="text-sm"><span className="text-muted-foreground">Adresse:</span> {req.pro_address}</p>
                                                            {req.pro_description && (
                                                                <p className="text-sm mt-2 text-muted-foreground">{req.pro_description}</p>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-col gap-2">
                                                            <div className="flex items-center gap-2">
                                                                <Input 
                                                                    type="number" 
                                                                    placeholder="% remise"
                                                                    className="w-24 h-9"
                                                                    id={`discount-${req.user_id}`}
                                                                    defaultValue="15"
                                                                />
                                                                <span className="text-sm text-muted-foreground">%</span>
                                                            </div>
                                                            <Button 
                                                                size="sm" 
                                                                className="rounded-full"
                                                                onClick={() => {
                                                                    const discount = parseFloat(document.getElementById(`discount-${req.user_id}`).value) || 15;
                                                                    handleProStatusChange(req.user_id, 'approve', discount);
                                                                }}
                                                            >
                                                                <CheckCircle className="h-4 w-4 mr-1" />
                                                                Approuver
                                                            </Button>
                                                            <Button 
                                                                size="sm" 
                                                                variant="destructive"
                                                                className="rounded-full"
                                                                onClick={() => handleProStatusChange(req.user_id, 'reject')}
                                                            >
                                                                <XCircle className="h-4 w-4 mr-1" />
                                                                Refuser
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Approved Pro Accounts */}
                            <Card className="glass rounded-2xl">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Briefcase className="h-5 w-5 text-primary" />
                                        Comptes professionnels actifs
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {users.filter(u => u.is_pro).length === 0 ? (
                                            <p className="text-center text-muted-foreground py-8">Aucun compte professionnel actif</p>
                                        ) : (
                                            users.filter(u => u.is_pro).map(proUser => (
                                                <div key={proUser.user_id} className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <h4 className="font-semibold">{proUser.company_name || proUser.name}</h4>
                                                            <Badge className="bg-emerald-500/10 text-emerald-500 border-0">
                                                                <Percent className="h-3 w-3 mr-1" />
                                                                {proUser.discount_percentage}%
                                                            </Badge>
                                                        </div>
                                                        <p className="text-sm text-muted-foreground">{proUser.email}</p>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Input 
                                                            type="number" 
                                                            className="w-20 h-9"
                                                            defaultValue={proUser.discount_percentage}
                                                            id={`edit-discount-${proUser.user_id}`}
                                                        />
                                                        <Button 
                                                            size="sm" 
                                                            variant="secondary"
                                                            className="rounded-full"
                                                            onClick={() => {
                                                                const discount = parseFloat(document.getElementById(`edit-discount-${proUser.user_id}`).value) || 0;
                                                                handleProStatusChange(proUser.user_id, 'update_discount', discount);
                                                            }}
                                                        >
                                                            Modifier %
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* Products */}
                    <TabsContent value="products">
                        <Card className="glass rounded-2xl">
                            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
                                <CardTitle>Gestion des produits</CardTitle>
                                <div className="flex items-center gap-2">
                                    <Button 
                                        variant="outline" 
                                        className="rounded-full"
                                        onClick={async () => {
                                            try {
                                                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/products`, {
                                                    headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
                                                });
                                                if (!response.ok) throw new Error('Erreur');
                                                const blob = await response.blob();
                                                const url = window.URL.createObjectURL(blob);
                                                const a = document.createElement('a');
                                                a.href = url;
                                                a.download = `produits_oca_${new Date().toISOString().split('T')[0]}.csv`;
                                                document.body.appendChild(a);
                                                a.click();
                                                window.URL.revokeObjectURL(url);
                                                a.remove();
                                                toast.success('Export CSV téléchargé');
                                            } catch (error) {
                                                toast.error('Erreur lors de l\'export');
                                            }
                                        }}
                                    >
                                        <Download className="h-4 w-4 mr-2" />
                                        Export CSV
                                    </Button>
                                    <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
                                        <DialogTrigger asChild>
                                            <Button className="rounded-full" onClick={() => { setEditingProduct(null); resetProductForm(); }}>
                                                <Plus className="h-4 w-4 mr-2" />
                                                Ajouter un produit
                                            </Button>
                                        </DialogTrigger>
                                    <DialogContent className="max-w-2xl">
                                        <DialogHeader>
                                            <DialogTitle>{editingProduct ? 'Modifier le produit' : 'Nouveau produit'}</DialogTitle>
                                        </DialogHeader>
                                        <div className="grid gap-4 py-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label>Nom</Label>
                                                    <Input value={productForm.name} onChange={(e) => setProductForm(p => ({ ...p, name: e.target.value }))} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Marque</Label>
                                                    <Input value={productForm.brand} onChange={(e) => setProductForm(p => ({ ...p, brand: e.target.value }))} />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Description</Label>
                                                <Textarea value={productForm.description} onChange={(e) => setProductForm(p => ({ ...p, description: e.target.value }))} />
                                            </div>
                                            <div className="grid grid-cols-3 gap-4">
                                                <div className="space-y-2">
                                                    <Label>Catégorie</Label>
                                                    <Select value={productForm.category} onValueChange={(v) => setProductForm(p => ({ ...p, category: v }))}>
                                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="chimie">Chimie</SelectItem>
                                                            <SelectItem value="materiel">Matériel</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Prix (€)</Label>
                                                    <Input type="number" step="0.01" value={productForm.price} onChange={(e) => setProductForm(p => ({ ...p, price: e.target.value }))} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Stock</Label>
                                                    <Input type="number" value={productForm.stock} onChange={(e) => setProductForm(p => ({ ...p, stock: e.target.value }))} />
                                                </div>
                                            </div>
                                            
                                            {/* Image Upload Section */}
                                            <div className="space-y-2">
                                                <Label>Image du produit</Label>
                                                <div className="flex gap-4">
                                                    {/* Preview */}
                                                    <div className="relative w-32 h-32 rounded-xl overflow-hidden bg-muted flex items-center justify-center border-2 border-dashed border-border">
                                                        {imagePreview || productForm.image_url ? (
                                                            <>
                                                                <img 
                                                                    src={imagePreview || productForm.image_url} 
                                                                    alt="Preview" 
                                                                    className="w-full h-full object-cover"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={clearImagePreview}
                                                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                                                >
                                                                    <X className="h-3 w-3" />
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <Image className="h-8 w-8 text-muted-foreground" />
                                                        )}
                                                    </div>
                                                    
                                                    {/* Upload Controls */}
                                                    <div className="flex-1 space-y-3">
                                                        <div>
                                                            <input
                                                                type="file"
                                                                accept="image/jpeg,image/png,image/gif,image/webp"
                                                                onChange={handleImageUpload}
                                                                className="hidden"
                                                                id="image-upload"
                                                                disabled={uploadingImage}
                                                            />
                                                            <label htmlFor="image-upload">
                                                                <Button 
                                                                    type="button" 
                                                                    variant="secondary" 
                                                                    className="w-full rounded-xl cursor-pointer"
                                                                    disabled={uploadingImage}
                                                                    asChild
                                                                >
                                                                    <span>
                                                                        {uploadingImage ? (
                                                                            <>
                                                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                                                Upload en cours...
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Upload className="h-4 w-4 mr-2" />
                                                                                Choisir une image
                                                                            </>
                                                                        )}
                                                                    </span>
                                                                </Button>
                                                            </label>
                                                            <p className="text-xs text-muted-foreground mt-1">JPG, PNG, GIF, WebP - Max 5MB</p>
                                                        </div>
                                                        
                                                        <div className="relative">
                                                            <div className="absolute inset-0 flex items-center">
                                                                <span className="w-full border-t" />
                                                            </div>
                                                            <div className="relative flex justify-center text-xs uppercase">
                                                                <span className="bg-background px-2 text-muted-foreground">ou URL</span>
                                                            </div>
                                                        </div>
                                                        
                                                        <Input 
                                                            placeholder="https://exemple.com/image.jpg" 
                                                            value={productForm.image_url} 
                                                            onChange={(e) => {
                                                                setProductForm(p => ({ ...p, image_url: e.target.value }));
                                                                setImagePreview(e.target.value);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="space-y-2">
                                                <Label>Info dosage (optionnel)</Label>
                                                <Input value={productForm.dosage_info} onChange={(e) => setProductForm(p => ({ ...p, dosage_info: e.target.value }))} placeholder="Ex: 100g pour +0.2 pH par 50m³" />
                                            </div>
                                            <Button onClick={handleProductSubmit} className="rounded-full">
                                                {editingProduct ? 'Mettre à jour' : 'Créer le produit'}
                                            </Button>
                                        </div>
                                    </DialogContent>
                                </Dialog>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {products.map(product => (
                                        <div key={product.product_id} className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                                            <img src={product.image_url} alt={product.name} className="h-16 w-16 rounded-xl object-cover" />
                                            <div className="flex-1">
                                                <p className="font-semibold">{product.name}</p>
                                                <p className="text-sm text-muted-foreground">{product.brand} • {product.category}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-bold text-primary">{formatPrice(product.price)}</p>
                                                <p className="text-xs text-muted-foreground">Stock: {product.stock}</p>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button size="sm" variant="secondary" className="rounded-full" onClick={() => openEditProduct(product)}>
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button size="sm" variant="destructive" className="rounded-full" onClick={() => handleDeleteProduct(product.product_id)}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Orders Management */}
                    <TabsContent value="orders">
                        <AdminOrdersSection />
                    </TabsContent>

                    {/* Sales Report */}
                    <TabsContent value="sales">
                        <SalesReportSection />
                    </TabsContent>

                    {/* Loyalty Points Management */}
                    <TabsContent value="loyalty">
                        <div className="space-y-6">
                            {/* Add/Remove Points Section */}
                            <Card className="glass rounded-2xl">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Gift className="h-5 w-5 text-amber-500" />
                                        Gérer les Points de Fidélité
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {/* Action toggle */}
                                    <div className="flex gap-2 mb-4" data-testid="loyalty-action-toggle">
                                        <Button
                                            type="button"
                                            variant={loyaltyAction === 'add' ? 'default' : 'outline'}
                                            onClick={() => setLoyaltyAction('add')}
                                            className="rounded-full gap-2"
                                            data-testid="loyalty-action-add"
                                        >
                                            <Plus className="h-4 w-4" />
                                            Ajouter des points
                                        </Button>
                                        <Button
                                            type="button"
                                            variant={loyaltyAction === 'remove' ? 'default' : 'outline'}
                                            onClick={() => setLoyaltyAction('remove')}
                                            className={`rounded-full gap-2 ${loyaltyAction === 'remove' ? 'bg-red-500 hover:bg-red-600 text-white' : ''}`}
                                            data-testid="loyalty-action-remove"
                                        >
                                            <Minus className="h-4 w-4" />
                                            Retirer des points (achat magasin)
                                        </Button>
                                    </div>

                                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                        <div>
                                            <Label>Utilisateur</Label>
                                            <Select 
                                                value={loyaltySelectedUser} 
                                                onValueChange={setLoyaltySelectedUser}
                                            >
                                                <SelectTrigger className="mt-1" data-testid="loyalty-user-select">
                                                    <SelectValue placeholder="Sélectionner un client" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {users.map(u => (
                                                        <SelectItem key={u.user_id} value={u.user_id}>
                                                            {u.name} ({u.email}) - {u.loyalty_points || 0} pts
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label>{loyaltyAction === 'add' ? 'Points à attribuer' : 'Points à retirer'}</Label>
                                            <Input
                                                type="number"
                                                min="1"
                                                value={loyaltyPointsToAdd}
                                                onChange={(e) => setLoyaltyPointsToAdd(e.target.value)}
                                                placeholder="Ex: 10"
                                                className="mt-1"
                                                data-testid="loyalty-points-input"
                                            />
                                        </div>
                                        <div>
                                            <Label>Raison</Label>
                                            <Input
                                                value={loyaltyReason}
                                                onChange={(e) => setLoyaltyReason(e.target.value)}
                                                placeholder={loyaltyAction === 'add' ? 'Ex: Bonus bienvenue' : 'Ex: Utilisation en magasin'}
                                                className="mt-1"
                                                data-testid="loyalty-reason-input"
                                            />
                                        </div>
                                        <div className="flex items-end">
                                            <Button
                                                onClick={async () => {
                                                    if (!loyaltySelectedUser || !loyaltyPointsToAdd) {
                                                        toast.error('Veuillez sélectionner un utilisateur et un nombre de points');
                                                        return;
                                                    }
                                                    setLoyaltyLoading(true);
                                                    try {
                                                        const endpoint = loyaltyAction === 'add'
                                                            ? '/admin/loyalty/add-points'
                                                            : '/admin/loyalty/remove-points';
                                                        const defaultReason = loyaltyAction === 'add'
                                                            ? 'Attribution manuelle'
                                                            : 'Utilisation en magasin';
                                                        const result = await apiRequest(endpoint, {
                                                            method: 'POST',
                                                            body: JSON.stringify({
                                                                user_id: loyaltySelectedUser,
                                                                points: parseInt(loyaltyPointsToAdd),
                                                                reason: loyaltyReason || defaultReason
                                                            })
                                                        });
                                                        toast.success(result.message);
                                                        setLoyaltySelectedUser('');
                                                        setLoyaltyPointsToAdd('');
                                                        setLoyaltyReason('');
                                                        loadUsers();
                                                    } catch (error) {
                                                        toast.error(error.message);
                                                    } finally {
                                                        setLoyaltyLoading(false);
                                                    }
                                                }}
                                                disabled={loyaltyLoading || !loyaltySelectedUser || !loyaltyPointsToAdd}
                                                className={`w-full rounded-full ${loyaltyAction === 'remove' ? 'bg-red-500 hover:bg-red-600 text-white' : ''}`}
                                                data-testid="loyalty-submit-btn"
                                            >
                                                {loyaltyLoading ? (
                                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                ) : loyaltyAction === 'add' ? (
                                                    <Coins className="h-4 w-4 mr-2" />
                                                ) : (
                                                    <Minus className="h-4 w-4 mr-2" />
                                                )}
                                                {loyaltyAction === 'add' ? 'Attribuer les points' : 'Retirer les points'}
                                            </Button>
                                        </div>
                                    </div>
                                    
                                    <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                                        <p className="text-sm text-muted-foreground">
                                            <strong>Rappel :</strong> 1 point = 1€ de réduction. <strong>Ajouter</strong> : pour récompenser un client. <strong>Retirer</strong> : quand le client utilise ses points en magasin à Flayosc (paiement comptoir).
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Users with Points */}
                            <Card className="glass rounded-2xl">
                                <CardHeader>
                                    <CardTitle>Solde des Points par Client</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {users.filter(u => (u.loyalty_points || 0) > 0).length === 0 ? (
                                            <p className="text-center text-muted-foreground py-8">
                                                Aucun client n&apos;a de points de fidélité pour le moment.
                                            </p>
                                        ) : (
                                            users
                                                .filter(u => (u.loyalty_points || 0) > 0)
                                                .sort((a, b) => (b.loyalty_points || 0) - (a.loyalty_points || 0))
                                                .map(u => (
                                                    <div key={u.user_id} className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl" data-testid={`loyalty-row-${u.user_id}`}>
                                                        {u.picture ? (
                                                            <img src={u.picture} alt={u.name} className="h-12 w-12 rounded-full" />
                                                        ) : (
                                                            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                                                                <Users className="h-6 w-6 text-primary" />
                                                            </div>
                                                        )}
                                                        <div className="flex-1">
                                                            <p className="font-semibold">{u.name}</p>
                                                            <p className="text-sm text-muted-foreground">{u.email}</p>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="flex items-center gap-2 text-amber-500">
                                                                <Coins className="h-5 w-5" />
                                                                <span className="font-bold text-xl">{u.loyalty_points || 0}</span>
                                                            </div>
                                                            <p className="text-xs text-muted-foreground">
                                                                = {u.loyalty_points || 0}€ de réduction
                                                            </p>
                                                        </div>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="rounded-full gap-1 border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                                                            data-testid={`loyalty-quick-remove-${u.user_id}`}
                                                            onClick={async () => {
                                                                const raw = window.prompt(`Combien de points retirer à ${u.name} ? (solde actuel : ${u.loyalty_points || 0} pts)`, '');
                                                                if (!raw) return;
                                                                const amount = parseInt(raw, 10);
                                                                if (!Number.isFinite(amount) || amount <= 0) {
                                                                    toast.error('Nombre de points invalide');
                                                                    return;
                                                                }
                                                                const reason = window.prompt('Raison (optionnel) :', 'Utilisation en magasin') || 'Utilisation en magasin';
                                                                try {
                                                                    const result = await apiRequest('/admin/loyalty/remove-points', {
                                                                        method: 'POST',
                                                                        body: JSON.stringify({ user_id: u.user_id, points: amount, reason })
                                                                    });
                                                                    toast.success(result.message);
                                                                    loadUsers();
                                                                } catch (error) {
                                                                    toast.error(error.message);
                                                                }
                                                            }}
                                                        >
                                                            <Minus className="h-3.5 w-3.5" />
                                                            Retirer
                                                        </Button>
                                                    </div>
                                                ))
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* Users */}
                    <TabsContent value="users">
                        <Card className="glass rounded-2xl">
                            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
                                <CardTitle>Utilisateurs inscrits ({users.length})</CardTitle>
                                <Button 
                                    variant="outline" 
                                    className="rounded-full"
                                    onClick={async () => {
                                        try {
                                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/users`, {
                                                headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
                                            });
                                            if (!response.ok) throw new Error('Erreur');
                                            const blob = await response.blob();
                                            const url = window.URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = `utilisateurs_oca_${new Date().toISOString().split('T')[0]}.csv`;
                                            document.body.appendChild(a);
                                            a.click();
                                            window.URL.revokeObjectURL(url);
                                            a.remove();
                                            toast.success('Export CSV téléchargé');
                                        } catch (error) {
                                            toast.error('Erreur lors de l\'export');
                                        }
                                    }}
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Export CSV
                                </Button>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {users.map(u => (
                                        <div key={u.user_id} className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                                            {u.picture ? (
                                                <img src={u.picture} alt={u.name} className="h-12 w-12 rounded-full" />
                                            ) : (
                                                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                                                    <Users className="h-6 w-6 text-primary" />
                                                </div>
                                            )}
                                            <div className="flex-1">
                                                <p className="font-semibold">{u.name}</p>
                                                <p className="text-sm text-muted-foreground">{u.email}</p>
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                Inscrit le {formatDate(u.created_at)}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Marketing */}
                    <TabsContent value="marketing">
                        <MarketingAdminPage />
                    </TabsContent>

                    {/* Settings */}
                    <TabsContent value="settings">
                        <SettingsSection />
                    </TabsContent>

                    {/* Bulk Image Upload */}
                    <TabsContent value="images">
                        <BulkImageUpload />
                    </TabsContent>

                    {/* Articles / Blog */}
                    <TabsContent value="articles">
                        <ArticlesSection />
                    </TabsContent>

                    {/* SpinTouch */}
                    <TabsContent value="spintouch">
                        <SpinTouchSection />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
